<template>
  <vs-popup class="manage-so-lead-time-popup"
            id="popup"
            :title="isEditMode ? `MANAGE SALES ORDER LEAD TIME - ${selected_region}` : 'SET SALES ORDER LEAD TIME'"
            @close="open = false"
            :active.sync="open"
            :buttons-hidden="true">
    <vx-card no-shadow>
      <ValidationObserver ref="form" v-slot="{ handleSubmit }">
        <form id="manage-so-lead-time-form" @submit.prevent="handleSubmit(onClickSave)" autocomplete="off">

          <!-- Region Display (Edit Mode) -->
          <div class="mb-5 region-banner p-3 rounded-lg flex items-center justify-between" v-if="isEditMode">
            <div class="flex items-center">
              <div class="icon-wrapper mr-3 flex items-center justify-center rounded-full">
                <feather-icon icon="GlobeIcon" svgClasses="h-5 w-5 text-primary" />
              </div>
              <div>
                <span class="text-xs uppercase tracking-wide font-semibold text-muted block">Target Region</span>
                <span class="text-base font-bold text-primary">{{ selected_region }}</span>
              </div>
            </div>
            <vs-chip transparent color="primary" class="font-semibold text-xs">
              Configured Region
            </vs-chip>
          </div>

          <!-- Region Selection (Create Mode) -->
          <div class="mb-5 vx-row" v-else>
            <div class="w-full vx-col">
              <ValidationProvider vid="region" name="Region" rules="required" v-slot="{ errors }">
                <small class="label font-semibold">Region</small>
                <v-select
                  v-model="selected_region"
                  :options="regionOptions"
                  :reduce="option => option.value"
                  label="label"
                  placeholder="Select Region"
                  class="w-full mt-1"
                />
                <span class="text-danger text-sm" v-if="errors[0]">{{ errors[0] }}</span>
              </ValidationProvider>
            </div>
          </div>

          <!-- Lead Time Ranges Section Title -->
          <div class="vx-row mb-3 items-center">
            <div class="vx-col w-full">
              <span class="text-xs uppercase tracking-wider font-semibold text-grey">
                Lead Time Ranges
              </span>
            </div>
          </div>

          <!-- Ranges Header -->
          <div class="vx-row mb-2">
            <div class="vx-col w-2/5">
              <small class="label font-semibold">From (Days)</small>
            </div>
            <div class="vx-col w-2/5">
              <small class="label font-semibold">To (Days)</small>
            </div>
            <div class="vx-col w-1/5 text-center">
              <small class="label font-semibold">Action</small>
            </div>
          </div>

          <!-- Range Rows -->
          <div class="mb-3 vx-row items-center" v-for="(range, index) in ranges" :key="index">
            <div class="w-full vx-col md:w-2/5 md:mb-0">
              <ValidationProvider
                :vid="'from_' + index"
                :name="'From (' + (index + 1) + ')'"
                :rules="validate_from(index)"
                v-slot="{ errors }"
              >
                <vs-input
                  type="number"
                  v-model.number="range.from"
                  name="from"
                  placeholder="e.g. 1"
                  class="w-full"
                  :danger="!!errors[0]"
                  :danger-text="errors[0]"
                  val-icon-danger="icon-x"
                  val-icon-pack="feather"
                />
              </ValidationProvider>
            </div>

            <div class="w-full vx-col md:w-2/5 md:mb-0">
              <ValidationProvider
                :vid="'to_' + index"
                :name="'To (' + (index + 1) + ')'"
                :rules="validate_to(index)"
                v-slot="{ errors }"
              >
                <vs-input
                  type="number"
                  v-model.number="range.to"
                  name="to"
                  placeholder="e.g. 5"
                  class="w-full"
                  :danger="!!errors[0]"
                  :danger-text="errors[0]"
                  val-icon-danger="icon-x"
                  val-icon-pack="feather"
                />
              </ValidationProvider>
            </div>

            <div class="w-full vx-col md:w-1/5 md:mb-0 text-center">
              <vs-button
                :disabled="ranges.length <= 1"
                @click="deleteRange(index)"
                size="small"
                color="danger"
                type="filled"
                icon-pack="feather"
                icon="icon-x"
              />
            </div>
          </div>

          <vs-button
            @click="addRange"
            size="small"
            color="success"
            type="filled"
            icon-pack="feather"
            class="mt-2"
            icon="icon-plus"
          >
            Add Range
          </vs-button>

          <!-- Save & Reset Button -->
          <div class="flex flex-wrap items-center justify-end mt-8">
            <vs-button
              v-can="'edit-so-lead-time-setting'"
              button="submit"
              class="mt-2 ml-auto"
            >
              Save Changes
            </vs-button>
            <vs-button
              @click="onClickReset"
              class="mt-2 ml-4"
              type="border"
              color="warning"
            >
              Reset
            </vs-button>
          </div>
        </form>
      </ValidationObserver>
    </vx-card>
  </vs-popup>
</template>

<script>
import { RepositoryFactory } from '@/repository/RepositoryFactory'
import vSelect from 'vue-select'
import SuccessMessage from '@/mixins/SuccessMessage'
import ErrorMessages from '@/mixins/ErrorMessages'

const SalesOrderLeadTimeRepository = RepositoryFactory.get('so_lead_time')

function initialState() {
  return {
    open: false,
    selected_region: '',
    fixedRegion: false,
    isEditMode: false,
    ranges: [{ from: '', to: '' }],
    regionOptions: [],
    original_data: null
  }
}

const DEFAULT_REGIONS = ['Great Britain', 'Europe', 'ROW']

export default {
  name: 'ManageSoLeadTimePopUp',
  mixins: [SuccessMessage, ErrorMessages],
  components: {
    'v-select': vSelect
  },
  data() {
    return initialState()
  },
  methods: {
    async openPopup(regionData = null, allRegions = DEFAULT_REGIONS) {
      try {
        Object.assign(this.$data, initialState())
        if (this.$refs.form) {
          this.$refs.form.reset()
        }

        const regionsList = (allRegions && allRegions.length > 0) ? allRegions : DEFAULT_REGIONS
        this.regionOptions = regionsList.map(r => {
          const name = typeof r === 'string' ? r : (r.name || r.region || '')
          return { label: name, value: name }
        })

        if (regionData) {
          this.original_data = JSON.parse(JSON.stringify(regionData))
          const regionName = regionData.region || regionData.name || ''
          this.selected_region = regionName
          this.fixedRegion = true
          this.isEditMode = true

          if (regionData.ranges && Array.isArray(regionData.ranges) && regionData.ranges.length > 0) {
            this.ranges = JSON.parse(JSON.stringify(regionData.ranges))
          } else {
            this.ranges = [{ from: '', to: '' }]
          }
        } else {
          this.isEditMode = false
          this.fixedRegion = false
          this.ranges = [{ from: '', to: '' }]
          if (this.regionOptions.length > 0) {
            this.selected_region = this.regionOptions[0].value
          }
        }

        this.open = true
      } catch (error) {
        console.error(error)
      }
    },
    validate_from(index) {
      if (index === 0) {
        return `range_from_less_than_or_equal:@to_${index}|required`
      }
      const previousIndex = index - 1
      return `range_from_less_than_or_equal:@to_${index}|range_cross_over_greater:@to_${previousIndex}|required`
    },
    validate_to(index) {
      return `range_to_greater_than_or_equal:@from_${index}|required`
    },
    addRange() {
      this.ranges.push({ from: '', to: '' })
    },
    deleteRange(index) {
      if (this.ranges.length > 1) {
        this.ranges.splice(index, 1)
      }
    },
    onClickReset() {
      if (this.original_data) {
        const regionName = this.original_data.region || this.original_data.name || ''
        this.selected_region = regionName
        if (this.original_data.ranges && Array.isArray(this.original_data.ranges) && this.original_data.ranges.length > 0) {
          this.ranges = JSON.parse(JSON.stringify(this.original_data.ranges))
        } else {
          this.ranges = [{ from: '', to: '' }]
        }
      } else {
        this.ranges = [{ from: '', to: '' }]
        if (this.regionOptions.length > 0) {
          this.selected_region = this.regionOptions[0].value
        }
      }
      if (this.$refs.form) {
        this.$refs.form.reset()
      }
    },
    async onClickSave() {
      const vue = this
      await this.$vs.loading({
        container: '#manage-so-lead-time-form',
        scale: 0.45
      })
      try {
        const formattedRanges = this.ranges
          .filter(r => r.from !== '' && r.to !== '')
          .map(r => ({
            from: parseInt(r.from, 10),
            to: parseInt(r.to, 10)
          }))

        const payload = {
          region: this.selected_region,
          ranges: formattedRanges
        }

        await SalesOrderLeadTimeRepository.saveSoLeadTime(payload)
        this.showSuccessMessage('Sales order lead time saved successfully!')
        if (vue.$parent && typeof vue.$parent.refreshData === 'function') {
          await vue.$parent.refreshData()
        }
        this.open = false
      } catch (e) {
        this.convertAndNotifyError(e)
      } finally {
        this.$vs.loading.close('#manage-so-lead-time-form > .con-vs-loading')
      }
    }
  }
}
</script>

<style scoped>
.manage-so-lead-time-popup >>> .vs-popup {
  min-width: 40% !important;
}

.region-banner {
  background: rgba(var(--vs-primary), 0.08);
  border: 1px solid rgba(var(--vs-primary), 0.18);
}

.icon-wrapper {
  width: 38px;
  height: 38px;
  background: rgba(var(--vs-primary), 0.15);
}

.text-muted {
  color: rgba(var(--vs-primary), 0.8);
}
</style>
